import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { PaperAbstractProps } from './types';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold, interRegular, interMedium } from "../../lib/fonts";
import LaTeXText from '../LaTeXText';
import { Trans } from '@lingui/react/macro';

/**
 * PaperAbstract displays the paper's abstract with refined editorial styling
 */
export default function PaperAbstract({ abstract }: PaperAbstractProps) {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={styles.container}>
            {/* Subtle section label */}
            <Text
                style={[
                    styles.label,
                    fontsLoaded && interMedium
                ]}
                accessibilityRole="header"
                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
            ><Trans id="papers.paperabstract.abstract" comment="UI copy in components/papers/PaperAbstract.tsx">
                Abstract
            </Trans></Text>

            {/* Abstract content with left accent */}
            <View style={styles.contentWrapper}>
                <View style={styles.accentLine} />
                <View style={styles.content}>
                    {abstract ? (
                        <LaTeXText
                            text={abstract}
                            style={[
                                styles.abstractText,
                                fontsLoaded && interRegular
                            ]}
                            fontsLoaded={fontsLoaded}
                            linkifyUrls
                        />
                    ) : (
                        <Text style={[styles.noAbstract, fontsLoaded && interRegular]}><Trans id="papers.paperabstract.abstractNotAvailable" comment="UI copy in components/papers/PaperAbstract.tsx">
                            Abstract not available
                        </Trans></Text>
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 24,
    },
    label: {
        fontSize: 11,
        fontWeight: '600',
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 10,
    },
    contentWrapper: {
        flexDirection: 'row',
    },
    accentLine: {
        width: 3,
        backgroundColor: colors.primaryGreen,
        borderRadius: 2,
        marginRight: 16,
        opacity: 0.7,
    },
    content: {
        flex: 1,
    },
    abstractText: {
        fontSize: 15,
        lineHeight: 26,
        color: colors.text700,
    },
    noAbstract: {
        fontSize: 15,
        lineHeight: 26,
        color: colors.text500,
        fontStyle: 'italic',
    },
});
